import { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { IndicatorName } from '@/api/rest/resources/types/fact';
import { INDICATOR_DOMAINS } from '@/api/rest/resources/types/IndicatorProduct';
import { RecommendationProductCode } from '@/api/rest/resources/types/recommendations';
import { CSV_REPORT_TYPES } from '@/api/rest/resources/types/reports';
import { DefaultWidgetNames, GraphTileType, WidgetType } from '@/api/rest/resources/types/widget';
import { useGetActivePlotGroup } from '@/components/widgets/config/PlotGroupFilters';
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import { useInSituIndicatorConfigurationOrgWidgetsByDomain } from '@/hooks/useInSituIndicatorConfigurationWidgets';
import { useActiveWidgetSlotConfiguration } from '@/hooks/useWidgetConfigurationByOrganizationId';
import { PlotRow } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plots/components/Cells';
import { CsvExportButton } from '@/pages/shared/reports/components/CsvExportButton';
import { isNullish } from '@/utils/booleans';
import { formatFactCarbonToCO2, formatFactKilogramsToTonnes } from '@/utils/formatting';

import { useIsLimitedAccessUser } from '../hooks/useIsLimitedAccessUser';
import { useAGCarbonCells, useBGCarbonCells, useDefaultCustomCells } from '../plots/hooks/useGetCustomCells';
import { LandPageLayout } from './components/LandPageLayout';

const PAGE_WIDGET_NAME = 'default_template_asset_carbon';
const PAGE_WIDGET_TYPE = WidgetType.t_page;

export const LandCarbonPage = () => {
  const { t } = useTranslation();

  const defaultWidgetConfiguration = useDefaultWidgetConfiguration();
  const customWidgetConfiguration = useActiveWidgetSlotConfiguration(
    PAGE_WIDGET_NAME,
    PAGE_WIDGET_TYPE,
    undefined,
    defaultWidgetConfiguration,
  );
  const widgetConfiguration = customWidgetConfiguration ?? defaultWidgetConfiguration;

  const customPlotListColumns: ColumnDef<PlotRow>[] = [
    ...useAGCarbonCells(),
    ...useBGCarbonCells(),
    ...useDefaultCustomCells(),
  ];

  return (
    <LandPageLayout
      signedInPageLayoutProps={{
        customizableWidgetTemplateProps: {
          widgetName: PAGE_WIDGET_NAME,
          widgetType: PAGE_WIDGET_TYPE,
          overrideLayoutConfiguration: defaultWidgetConfiguration,
        },
      }}
      navBarProps={{
        title: t('navbar.carbon'),
        children: <CsvExportButton reportType={CSV_REPORT_TYPES.CARBON_CSV} className='w-fit font-normal' />,
      }}
      plotListProps={{
        customColumns: customPlotListColumns,
      }}
    >
      <WidgetSlotRenderer nestedWidget={widgetConfiguration} />
    </LandPageLayout>
  );
};

const useDefaultWidgetConfiguration = () => {
  const { t } = useTranslation();

  const isLimitedAccessUser = useIsLimitedAccessUser();
  const activeFilters = useGetActivePlotGroup();
  const showSummaryCard = !isLimitedAccessUser && !isNullish(activeFilters) && activeFilters.length === 0;
  const inSituWidgets = useInSituIndicatorConfigurationOrgWidgetsByDomain(INDICATOR_DOMAINS.CARBON);

  const abcHeaderProps = {
    title: t('shared.ncaDetail.details.aboveGroundCarbon.labels.aboveGroundCarbonStorage'),
    headerTooltip: t('shared.ncaDetail.details.carbon.explainers.aboveTheGroundCarbonStorage.body'),
    showToggle: true,
    showZoom: true,
    showGraphType: true,
  };

  const bgcHeaderProps = {
    title: t('shared.ncaDetail.details.carbon.labels.belowTheGroundCarbonStorage'),
    headerTooltip: t('shared.ncaDetail.details.carbon.explainers.belowTheGroundCarbonStorage.body'),
    showToggle: true,
    showZoom: true,
    showGraphType: true,
  };

  return {
    name: PAGE_WIDGET_NAME,
    type: PAGE_WIDGET_TYPE,
    widgets: [
      {
        name: 'carbon_asset_summary_row',
        type: WidgetType.a_stack,
        visible: true,
        relatedData: {
          direction: 'row',
          center: true,
          spacing: 6,
          className: 'flex-col lg:flex-row lg:flex-wrap items-stretch',
        },
        widgets: [
          {
            name: DefaultWidgetNames.default_fact_card_carbon_total,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: IndicatorName.carbon_combined_per_ha,
              headerText: 'global.widget.combinedCarbon',
              tooltipText: 'global.widget.combinedCarbonTooltip',
              formatter: formatFactKilogramsToTonnes,
            },
            visible: true,
          },
          {
            name: DefaultWidgetNames.default_fact_card_current_bgc_co2,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: IndicatorName.carbon_combined_per_ha,
              headerText: 'global.widget.currentCombinedCo2Equivalent',
              tooltipText: 'global.widget.currentCombinedCo2EquivalentTooltip',
              formatter: formatFactCarbonToCO2,
            },
            visible: true,
          },
          {
            name: DefaultWidgetNames.default_fact_card_historic_carbon_5y_uplift,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: IndicatorName.carbon_combined_previous_5y_pct_change,
              headerText: 'global.widget.historic5yAvgCarbonUplift',
              tooltipText: 'global.widget.historic5yUpliftTooltip',
              formatter: formatFactKilogramsToTonnes,
            },
            visible: true,
          },
          {
            name: DefaultWidgetNames.default_fact_card_historic_carbon_1y_uplift,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: IndicatorName.carbon_combined_previous_1y_pct_change,
              headerText: 'global.widget.yearlyUpliftOneYear',
              tooltipText: 'global.widget.projection1yUpliftTooltip',
            },
            visible: true,
          },
        ],
      },
      {
        name: 'carbon_summary',
        type: WidgetType.o_recommendation_summary,
        visible: showSummaryCard,
        relatedData: {
          productCode: RecommendationProductCode.EK_CARBON,
        },
      },
      {
        name: 'asset_agc_indicator_card',
        type: WidgetType.o_details_graph_card,
        visible: true,
        relatedData: {
          headerProps: abcHeaderProps,
          graphTileType: GraphTileType.historic_above_ground_carbon,
        },
      },
      {
        name: 'asset_bgc_indicator_card',
        type: WidgetType.o_details_graph_card,
        visible: true,
        relatedData: {
          headerProps: bgcHeaderProps,
          graphTileType: GraphTileType.historic_below_ground_carbon,
        },
      },
      ...inSituWidgets,
    ],
  };
};
